import { Box } from '@mui/material';
import {
  activeSuspensions,
  cardColor,
  breakRemaining,
  clockSeconds,
  deputyRemaining,
  formatClock,
  formatLongClock,
  formatScore,
  normalizeDisplay,
  penaltyMarks,
  periodDisplayName,
  periodOf,
  teamScore,
  timeoutSeconds,
  timeoutDimmed,
  timeoutShown,
  type DisplayLang,
  type DisplayPart,
  type PersonKind,
  type ScoreboardState,
  type TeamSide,
} from '@/components/scoreboard/engine';

interface Props {
  state: ScoreboardState;
  now: number;
}

const mono = '"DIN Alternate", "SF Mono", ui-monospace, Menlo, monospace';

const COPY: Record<DisplayLang, {
  players: string;
  no: string;
  name: string;
  goals: string;
  halfTime: string;
  eventFallback: string;
  suspension: string;
}> = {
  zh: {
    players: '球員',
    no: '號碼',
    name: '姓名',
    goals: '入球',
    halfTime: '中場休息',
    eventFallback: '手球賽事',
    suspension: '罰時',
  },
  en: {
    players: 'Players',
    no: 'No.',
    name: 'Name',
    goals: 'Goals',
    halfTime: 'Half time',
    eventFallback: 'Handball',
    suspension: 'Suspension',
  },
};

function periodLabel(name: string, lang: DisplayLang): string {
  return periodDisplayName(name, lang);
}

function sized(part: DisplayPart, size: string): string {
  return `calc(${part.scale} * ${size})`;
}

function markText(n: number): string {
  return n > 0 ? String(n) : '';
}

function SquadColumn({
  state,
  team,
  edge,
}: {
  state: ScoreboardState;
  team: TeamSide;
  edge: 'left' | 'right';
}) {
  const color = team === 'A' ? state.teamAColor : state.teamBColor;
  const players = team === 'A' ? state.playersA : state.playersB;
  const text = COPY[state.displayLang === 'en' ? 'en' : 'zh'];
  const look = normalizeDisplay(state.display).players;
  const rows = (people: Array<{ id: string; number: string; name: string; goals?: number }>) =>
    people.filter((p) => p.number.trim() || p.name.trim());

  return (
    <Box
      sx={{
        minWidth: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        px: '1vw',
        py: '1.2vh',
        borderRight: edge === 'left' ? '1px solid rgba(255,255,255,0.12)' : 'none',
        borderLeft: edge === 'right' ? '1px solid rgba(255,255,255,0.12)' : 'none',
        overflow: 'hidden',
        color: look.color,
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Box
          component="table"
          sx={{
            width: '100%',
            borderCollapse: 'collapse',
            '& th, & td': {
              py: '0.55vh',
              px: '0.35vw',
              textAlign: 'center',
              fontSize: sized(look, 'clamp(15px, 2.3vh, 34px)'),
              lineHeight: 1.2,
              borderBottom: '1px solid rgba(255,255,255,0.1)',
              verticalAlign: 'top',
            },
            '& th': {
              color: 'rgba(255,255,255,0.5)',
              fontWeight: 800,
              fontSize: sized(look, 'clamp(12px, 1.7vh, 24px)'),
            },
          }}
        >
          <thead>
            <tr>
              <th style={{ textAlign: 'left', width: '3.2vw' }}>{text.no}</th>
              <th style={{ textAlign: 'left' }}>{text.name}</th>
              <th style={{ width: '5.4vw', whiteSpace: 'nowrap' }}>{text.goals}</th>
              <th style={{ width: '3.2vw' }}>2′</th>
            </tr>
          </thead>
          <tbody>
            {rows(players).map((p) => {
              const marks = penaltyMarks(state, team, 'player' as PersonKind, p.number);
              return (
                <tr key={p.id}>
                  <td style={{ textAlign: 'left', fontWeight: 900, fontFamily: mono, color }}>{p.number || '—'}</td>
                  <td style={{ textAlign: 'left', fontWeight: 800, whiteSpace: 'normal', wordBreak: 'break-word' }}>{p.name || '—'}</td>
                  <td style={{ fontWeight: 900, fontFamily: mono }}>{p.goals || ''}</td>
                  <td style={{ fontWeight: 900, fontFamily: mono }}>{markText(marks.two)}</td>
                </tr>
              );
            })}
          </tbody>
        </Box>
      </Box>
    </Box>
  );
}

export default function ScoreboardBoard({ state, now }: Props) {
  const look = normalizeDisplay(state.display);
  const scoreA = teamScore(state, 'A');
  const scoreB = teamScore(state, 'B');
  const lang: DisplayLang = state.displayLang === 'en' ? 'en' : 'zh';
  const text = COPY[lang];
  const onBreak = state.breakVisible;
  const clockRunning = onBreak ? state.breakRunning : state.clockRunning;
  const clockValue = onBreak ? breakRemaining(state, now) : clockSeconds(state, now);
  const teamRemain = formatClock(Math.max(0, 60 - timeoutSeconds(state, now)));
  const subsA = activeSuspensions(state, now).filter((s) => s.team === 'A').slice(0, 4);
  const subsB = activeSuspensions(state, now).filter((s) => s.team === 'B').slice(0, 4);
  const cardsA = state.cards.filter((c) => c.team === 'A');
  const cardsB = state.cards.filter((c) => c.team === 'B');
  const periodLines = state.periods.slice(0, state.periodIndex + 1);
  const setTitle = onBreak ? text.halfTime : periodLabel(periodOf(state).name, lang);

  const timeoutRow = (team: TeamSide) => {
    const flags = timeoutShown(state, team);
    const dimmed = timeoutDimmed(state, team);
    const running = state.timeoutKind === team;
    const timer = (
      <Box
        sx={{
          minWidth: '4.6ch',
          textAlign: 'center',
          fontFamily: mono,
          fontWeight: 900,
          color: '#3ee0ff',
          fontSize: sized(look.timeout, 'clamp(22px, 4vh, 58px)'),
          lineHeight: 1,
          visibility: running ? 'visible' : 'hidden',
        }}
      >
        {teamRemain}
      </Box>
    );
    const badges = flags.map((on, i) => (
      <Box
        key={i}
        sx={{
          width: '3.2vw',
          minWidth: 34,
          height: '4.2vh',
          display: 'grid',
          placeItems: 'center',
          borderRadius: '0.5vh',
          border: '2px solid',
          borderColor: on ? look.timeout.color : 'rgba(255,255,255,0.28)',
          bgcolor: on ? look.timeout.color : 'transparent',
          color: on ? '#111' : 'rgba(255,255,255,0.35)',
          fontWeight: 900,
          fontFamily: mono,
          fontSize: sized(look.timeout, 'clamp(14px, 2.2vh, 32px)'),
          lineHeight: 1,
          opacity: !on && dimmed[i] ? 0.28 : 1,
        }}
      >
        {`T${i + 1}`}
      </Box>
    ));
    return (
      <Box sx={{ minHeight: '5.2vh', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.55vw' }}>
        {team === 'B' && timer}
        {badges}
        {team === 'A' && timer}
      </Box>
    );
  };

  const subList = (items: typeof subsA) => (
    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(52px, 4.2vw) minmax(132px, 11vw)',
          justifyItems: 'center',
          alignItems: 'baseline',
          columnGap: '0.8vw',
          rowGap: '0.7vh',
        }}
      >
        <Box sx={{ color: 'rgba(255,255,255,0.55)', fontWeight: 800, fontSize: 'clamp(12px, 1.7vh, 20px)', letterSpacing: '0.04em', lineHeight: 1 }}>
          {text.no}
        </Box>
        <Box sx={{ color: 'rgba(255,255,255,0.55)', fontWeight: 800, fontSize: 'clamp(12px, 1.7vh, 20px)', letterSpacing: '0.04em', lineHeight: 1 }}>
          {text.suspension}
        </Box>
        {items.map((s) => {
          const cell = {
            fontFamily: mono,
            fontWeight: 900,
            color: look.suspension.color,
            fontSize: sized(look.suspension, 'clamp(26px, 4.6vh, 68px)'),
            lineHeight: 1,
            textAlign: 'center',
          };
          return [
            <Box key={`${s.id}-no`} sx={cell}>{s.playerNo}</Box>,
            <Box key={`${s.id}-time`} sx={cell}>{formatClock(s.remainingSec)}</Box>,
          ];
        })}
      </Box>
    </Box>
  );

  const cardRow = (cards: typeof cardsA, align: 'flex-start' | 'flex-end') => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '0.6vw', justifyContent: align, alignContent: 'flex-end', minHeight: '8vh' }}>
      {cards.map((c) => (
        <Box
          key={c.id}
          sx={{
            minWidth: '4.2vw',
            height: '8vh',
            px: '0.4vw',
            borderRadius: '0.6vh',
            bgcolor: cardColor(c.kind),
            color: c.kind === 'yellow' ? '#1a1a1a' : '#fff',
            display: 'grid',
            placeItems: 'center',
            fontFamily: mono,
            fontWeight: 900,
            fontSize: sized(look.cards, 'clamp(20px, 3.8vh, 56px)'),
            lineHeight: 1,
          }}
        >
          {c.playerNo || c.playerName}
        </Box>
      ))}
    </Box>
  );

  return (
    <Box sx={{ height: '100%', width: '100%', bgcolor: '#05070b', color: '#f7f8fa', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {look.event.visible && (
        <Box
          sx={{
            flexShrink: 0,
            width: '100%',
            textAlign: 'center',
            fontWeight: 900,
            lineHeight: 1.05,
            px: '2vw',
            py: '1vh',
            color: look.event.color,
            fontSize: sized(look.event, 'clamp(28px, 4.6vh, 72px)'),
            borderBottom: '1px solid rgba(255,255,255,0.12)',
          }}
        >
          {state.eventName || text.eventFallback}
        </Box>
      )}
      <Box sx={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: look.players.visible ? '26% 48% 26%' : '1fr', overflow: 'hidden' }}>
        {look.players.visible && <SquadColumn state={state} team="A" edge="left" />}
        <Box sx={{ minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column', px: '1.2vw', py: '1vh' }}>
          {look.team.visible && (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'start', columnGap: '1vw' }}>
              <Box sx={{ textAlign: 'center', minWidth: 0 }}>
                <Box sx={{ color: state.teamAColor, fontWeight: 900, fontSize: sized(look.team, 'clamp(20px, 3.2vh, 48px)'), lineHeight: 1.05, wordBreak: 'break-word', minHeight: '1.2em' }}>
                  {state.teamAName}
                </Box>
              </Box>
              <Box sx={{ width: '2vw' }} />
              <Box sx={{ textAlign: 'center', minWidth: 0 }}>
                <Box sx={{ color: state.teamBColor, fontWeight: 900, fontSize: sized(look.team, 'clamp(20px, 3.2vh, 48px)'), lineHeight: 1.05, wordBreak: 'break-word', minHeight: '1.2em' }}>
                  {state.teamBName}
                </Box>
              </Box>
            </Box>
          )}

          {look.score.visible && (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'start', columnGap: '1vw' }}>
              <Box sx={{ textAlign: 'center', fontFamily: mono, fontWeight: 900, color: look.score.color, fontSize: sized(look.score, 'clamp(64px, 11vh, 168px)'), lineHeight: 0.86 }}>
                {formatScore(scoreA.goals)}
              </Box>
              <Box sx={{ fontFamily: mono, fontWeight: 800, color: 'rgba(255,255,255,0.45)', fontSize: sized(look.score, 'clamp(36px, 6vh, 90px)'), lineHeight: 0.8, pt: '1.6vh' }}>:</Box>
              <Box sx={{ textAlign: 'center', fontFamily: mono, fontWeight: 900, color: look.score.color, fontSize: sized(look.score, 'clamp(64px, 11vh, 168px)'), lineHeight: 0.86 }}>
                {formatScore(scoreB.goals)}
              </Box>
            </Box>
          )}

          {look.timeout.visible && (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', columnGap: '1vw', mt: '1.8vh' }}>
              {timeoutRow('A')}
              <Box sx={{ width: '2vw' }} />
              {timeoutRow('B')}
            </Box>
          )}

          {look.periodScores.visible && (
            <Box sx={{ textAlign: 'center' }}>
              {periodLines.map((p) => (
                <Box
                  key={p.id}
                  sx={{
                    fontWeight: 800,
                    color: look.periodScores.color,
                    fontSize: sized(look.periodScores, 'clamp(16px, 2.5vh, 36px)'),
                    lineHeight: 1.25,
                    fontFamily: mono,
                  }}
                >
                  {periodLabel(p.name, lang)} {formatScore(p.goalsA)} : {formatScore(p.goalsB)}
                </Box>
              ))}
            </Box>
          )}

          <Box sx={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: '1fr auto 1fr', columnGap: '2.4vw', alignItems: 'center', my: '0.6vh' }}>
            {look.suspension.visible && subsA.length > 0 ? subList(subsA) : <Box />}
            <Box sx={{ textAlign: 'center', px: '0.6vw' }}>
              {look.periodName.visible && (
                <Box sx={{ color: look.periodName.color, fontWeight: 900, fontSize: sized(look.periodName, 'clamp(22px, 4vh, 56px)'), lineHeight: 1, mb: '0.4vh' }}>
                  {setTitle}
                </Box>
              )}
              {look.clock.visible && (
                <Box
                  sx={{
                    fontFamily: mono,
                    fontWeight: 900,
                    fontSize: sized(look.clock, 'clamp(56px, 11vh, 168px)'),
                    lineHeight: 0.9,
                    color: clockRunning ? look.clock.color : '#ffb020',
                  }}
                >
                  {formatClock(clockValue)}
                </Box>
              )}
              {state.deputyVisible && (
                <Box sx={{ mt: '0.6vh', fontFamily: mono, fontWeight: 900, color: '#1edf2a', fontSize: 'clamp(28px, 5vh, 72px)', lineHeight: 1 }}>
                  {state.deputyMode === 'text' ? (state.deputyText || '副時鐘') : formatLongClock(deputyRemaining(state, now))}
                </Box>
              )}
            </Box>
            {look.suspension.visible && subsB.length > 0 ? subList(subsB) : <Box />}
          </Box>

          {look.cards.visible && (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1vw', alignItems: 'end' }}>
              {cardRow(cardsA, 'flex-start')}
              {cardRow(cardsB, 'flex-end')}
            </Box>
          )}
        </Box>
        {look.players.visible && <SquadColumn state={state} team="B" edge="right" />}
      </Box>
    </Box>
  );
}
